import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { submitLead } from '@/lib/api';
import { CheckCircle, Loader2, ArrowUpRight } from 'lucide-react';

export const LeadForm = ({ sourceForm = 'generic', compact = false, onSuccess, className = '' }) => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', company_size: '', inquiry_type: '', message: '', website: '',
  });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      await submitLead({ ...formData, source_page: location.pathname, source_form: sourceForm });
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', company_size: '', inquiry_type: '', message: '', website: '' });
      if (onSuccess) onSuccess();
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className={`text-center py-10 ${className}`}>
        <CheckCircle size={36} className="text-green-600 mx-auto mb-4" />
        <h4 className="text-lg font-semibold text-[#1A1008] mb-1">Thank you.</h4>
        <p className="text-[14px] text-[#6B5B50]">We'll get back to you shortly.</p>
      </div>
    );
  }

  const inputClass = "w-full px-4 py-3.5 rounded-xl bg-[#FAF5EF] border border-[rgba(61,28,17,0.08)] text-[14px] text-[#2D2019] placeholder:text-[#6B5B50]/40 focus:outline-none focus:ring-2 focus:ring-[rgba(232,100,44,0.2)] focus:border-[#E8642C]/40 transition-all";

  return (
    <form onSubmit={handleSubmit} className={className} data-testid="lead-capture-form">
      <input type="text" name="website" value={formData.website} onChange={handleChange} className="hidden" tabIndex={-1} autoComplete="off" />

      <div className={`grid gap-3.5 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
        <input name="name" value={formData.name} onChange={handleChange} placeholder="Your Name *" required className={inputClass} data-testid="lead-form-name-input" />
        <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Work Email *" required className={inputClass} data-testid="lead-form-email-input" />
        {!compact && (
          <>
            <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone (optional)" className={inputClass} />
            <input name="company" value={formData.company} onChange={handleChange} placeholder="Company *" required className={inputClass} data-testid="lead-form-company-input" />
            <select name="company_size" value={formData.company_size} onChange={handleChange} className={inputClass}>
              <option value="">Company Size</option>
              <option value="1-50">1-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="201-1000">201-1,000 employees</option>
              <option value="1001-5000">1,001-5,000 employees</option>
              <option value="5001+">5,001+ employees</option>
            </select>
            <select name="inquiry_type" value={formData.inquiry_type} onChange={handleChange} className={inputClass}>
              <option value="">What are you looking for?</option>
              <option value="consulting">Consulting</option>
              <option value="learning">Learning & Development</option>
              <option value="experiences">Experiences</option>
              <option value="engagement">Engagement & Research</option>
              <option value="botsai">BOTS.AI</option>
              <option value="other">Something else</option>
            </select>
            <div className="sm:col-span-2">
              <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Tell us more (optional)" rows={3} className={`${inputClass} resize-none`} />
            </div>
          </>
        )}
      </div>

      {errorMsg && <p className="text-[13px] text-[#991B1B] mt-3">{errorMsg}</p>}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="mt-5 w-full group bg-[#1A1008] text-white py-3.5 rounded-full text-[14px] font-medium tracking-wide hover:bg-[#3D1C11] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="lead-capture-submit-button"
      >
        {status === 'loading' ? (
          <><Loader2 size={16} className="animate-spin" /> Submitting...</>
        ) : (
          <>Start a Conversation <ArrowUpRight size={15} className="opacity-50 group-hover:opacity-80 transition-opacity" /></>
        )}
      </button>
    </form>
  );
};

export default LeadForm;
